// One day, on a time axis.
//
// The week deliberately has no time axis — barn work is blocked rather than
// clocked, and "who's on morning feed" is the useful question across seven
// days. A single day is the zoom where the axis earns its keep, because the
// questions change: is anybody here at two o'clock, and why does Dana's day
// have a hole in the middle of it.
//
// Two groupings, because the question has two halves and they need different
// rows:
//
//   By person   utilisation. A row per body, gaps drawn in. This is where an
//               underused half-day shows up.
//   By work     coverage. A row per kind of work. This is where a hole shows
//               up — an hour of Turnout with nothing in it.
//
// The ribbon along the top counts PEOPLE at each half hour, not shifts. One
// person doing morning and evening feed is one person, and a ribbon claiming
// two would be describing staff the barn doesn't have.

import { colorClass } from '../../lib/lessonTypes';
import {
  dayAxis,
  axisTicks,
  barGeometry,
  coverageSeries,
  gapsFor,
  tickLabel,
  toMinutes,
  timeRange,
  formatMinutes,
  minutesBetween,
  worstConflict,
  assignLanes,
} from '../../lib/shifts';

/** Rows are at least this tall so a bar is a comfortable target. */
const ROW = 'min-h-[3rem]';

export default function DayGantt({
  date,
  today,
  nowMinutes = null,
  shifts,
  staff,
  timeOff,
  lessons,
  // What the day NEEDS, each already carrying its own coverage from the API.
  requirements = [],
  groupBy = 'person',
  canEdit,
  onAdd,
  onOpen,
  // Null unless this day has a plan and the reader may change it.
  onClearPlan = null,
}) {
  const dayShifts = shifts.filter((s) => s.date === date);
  const dayOff = timeOff.filter((o) => date >= o.startDate && date <= o.endDate);
  const dayLessons = lessons.filter((l) => l.date === date);
  const dayReqs = requirements.filter((r) => r.date === date);
  const shortReqs = dayReqs.filter((r) => r.coverage?.short > 0);

  // The axis spans everything drawn on it, not just the shifts. A band for
  // "off 1-6" outside the axis would be clipped away, and the row would look
  // free at exactly the hours it isn't.
  const axis = dayAxis([
    ...dayShifts,
    ...dayOff.filter((o) => o.startTime && o.endTime),
    ...dayLessons.filter((l) => l.startTime && l.endTime),
    ...dayReqs,
  ]);
  const ticks = axisTicks(axis);
  const cover = coverageSeries(dayShifts, axis);
  const span = axis.end - axis.start;
  const pctOf = (min) => ((min - axis.start) / span) * 100;

  const rows =
    groupBy === 'person' ? personRows(staff, dayShifts) : workRows(dayShifts, dayReqs);

  return (
    <div className="card overflow-hidden">
      {/* The day's plan, and the only way to take it back off. It sits above
          the chart rather than on a requirement row because it removes ALL of
          them, and a control that empties a list should not look like it
          belongs to one line of it. */}
      {onClearPlan && (
        <div className="flex items-baseline justify-between gap-3 px-3 py-2 border-b border-ink-200 bg-hunter/5">
          <span className="text-xs text-hunter">
            Planned from a routine — {dayReqs.length} {dayReqs.length === 1 ? 'job' : 'jobs'} needed
            {shortReqs.length > 0 && (
              <span className="text-amber-700 font-medium">
                {', '}{shortReqs.length} short
              </span>
            )}
          </span>
          <button type="button" onClick={onClearPlan}
            className="shrink-0 text-xs text-ink-400 hover:text-rose-700 underline underline-offset-2">
            Clear this day&apos;s plan
          </button>
        </div>
      )}

      <div className="overflow-x-auto">
        <div className="min-w-[46rem]">
          {/* ---- axis ------------------------------------------------- */}
          <div className="flex border-b border-ink-200">
            <div className="w-40 shrink-0 px-3 py-2 text-xs font-medium uppercase tracking-wider text-ink-400">
              {groupBy === 'person' ? 'Who' : 'What'}
              {date === today && nowMinutes != null && (
                <span className="ml-2 normal-case tracking-normal text-rose-500">
                  now {tickLabel(nowMinutes)}
                </span>
              )}
            </div>
            <div className="relative flex-1 h-9">
              {ticks.map((t, i) => {
                const last = i === ticks.length - 1;
                return (
                  <span
                    key={t}
                    className={
                      'absolute top-1.5 text-[11px] text-ink-400 tabular-nums ' +
                      (last ? '' : '-translate-x-1/2')
                    }
                    style={last ? { right: 0 } : { left: `${pctOf(t)}%` }}
                  >
                    {tickLabel(t)}
                  </span>
                );
              })}
            </div>
          </div>

          {dayReqs.length > 0 && (
            <div
              className={
                'flex items-baseline gap-2 px-3 py-2 border-b text-sm ' +
                (shortReqs.length
                  ? 'bg-amber-50 border-amber-200 text-amber-900'
                  : 'bg-hunter/5 border-hunter/20 text-hunter')
              }
            >
              {shortReqs.length === 0 ? (
                <span>Everything this day needs is covered.</span>
              ) : (
                <>
                  <span className="font-medium">
                    {shortReqs.length} {shortReqs.length === 1 ? 'job is' : 'jobs are'} short
                  </span>
                  <span className="text-amber-800">
                    {shortReqs
                      .map(
                        (r) =>
                          `${r.typeName || 'Unspecified'} needs ${r.coverage.needed}, has ${r.coverage.covered}` +
                          (r.coverage.worstFrom
                            ? ` (${tickLabel(toMinutes(r.coverage.worstFrom))}–${tickLabel(toMinutes(r.coverage.worstTo))})`
                            : ''),
                      )
                      .join(' · ')}
                  </span>
                </>
              )}
              {groupBy === 'person' && (
                <span className="ml-auto text-xs opacity-70">Switch to By work to see where.</span>
              )}
            </div>
          )}

          {/* ---- how many people are on ------------------------------- */}
          <div className="flex border-b border-ink-100 bg-ink-50">
            <div className="w-40 shrink-0 px-3 py-1.5 text-[11px] text-ink-500">On at once</div>
            <div className="relative flex-1 h-7">
              {cover.map((c) => {
                const g = barGeometry(
                  { startTime: hhmm(c.start), endTime: hhmm(c.end) },
                  axis,
                );
                if (!g.width) return null;
                return (
                  <div
                    key={c.start}
                    className={
                      'absolute inset-y-1 flex items-center justify-center text-[10px] tabular-nums ' +
                      // Nobody on is the thing you are looking for, so it is the
                      // thing that is drawn loudest. An hour staffed only by an
                      // open shift is amber: planned, and by nobody.
                      (c.people === 0 && c.open === 0
                        ? 'bg-ink-100 text-ink-300'
                        : c.people === 0
                          ? 'bg-amber-100 text-amber-800'
                          : 'bg-hunter/15 text-hunter')
                    }
                    style={{ left: `${g.left}%`, width: `${g.width}%` }}
                    title={`${tickLabel(c.start)} — ${c.people} on${c.open ? `, ${c.open} unstaffed` : ''}`}
                  >
                    {c.people || (c.open ? '—' : '')}
                  </div>
                );
              })}
            </div>
          </div>

          {/* ---- rows -------------------------------------------------- */}
          {rows.length === 0 ? (
            <p className="px-3 py-6 text-sm text-ink-400">Nothing scheduled on this day.</p>
          ) : (
            rows.map((row) => (
              <Row
                key={row.key}
                row={row}
                axis={axis}
                ticks={ticks}
                pctOf={pctOf}
                nowMinutes={date === today ? nowMinutes : null}
                reqs={row.reqs ?? []}
                off={row.userId ? dayOff.filter((o) => o.userId === row.userId) : []}
                teaching={row.userId ? dayLessons.filter((l) => l.userId === row.userId) : []}
                canEdit={canEdit}
                onFill={(prefill) => onAdd(date, row.userId ?? null, prefill)}
                onOpen={onOpen}
                showNames={groupBy === 'work'}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function Row({
  row, axis, ticks, pctOf, nowMinutes, reqs, off, teaching, canEdit, onFill, onOpen, showNames,
}) {
  const worked = row.shifts.reduce((n, s) => n + minutesBetween(s.startTime, s.endTime), 0);
  // Only meaningful per person: "a hole in the Turnout row" is a coverage gap,
  // which the ribbon above already says better than a grey bar would.
  const holes = row.userId ? gapsFor(row.shifts) : [];
  // Two people on evening feed is the answer to "does one person cover this",
  // and at identical geometry the second bar is invisible behind the first.
  const { lanes, laneOf } = assignLanes(row.shifts);

  return (
    // The row grows with its lanes rather than squeezing them: three people on
    // one job at 14px each is a chart nobody can read.
    <div
      className={'flex border-b border-ink-100 last:border-0 ' + ROW}
      style={lanes > 1 ? { minHeight: `${1.5 + lanes * 2}rem` } : undefined}
    >
      <div className="w-40 shrink-0 px-3 py-2">
        <span
          className={
            'block text-sm font-medium break-words ' + (row.open ? 'text-amber-700' : 'text-ink-900')
          }
        >
          {row.name}
        </span>
        <span className="block text-xs text-ink-400">
          {formatMinutes(worked)}
          {holes.length > 0 && ` · ${holes.length === 1 ? 'a gap' : `${holes.length} gaps`}`}
        </span>
        {reqs.length > 0 && (
          <span
            className={
              'block text-xs ' +
              (reqs.some((r) => r.coverage?.short > 0) ? 'text-rose-700 font-medium' : 'text-hunter')
            }
          >
            needs {reqs.reduce((n, r) => n + (r.coverage?.needed ?? 0), 0)}, has{' '}
            {reqs.reduce((n, r) => n + Math.min(r.coverage?.covered ?? 0, r.coverage?.needed ?? 0), 0)}
          </span>
        )}
      </div>

      <div className="relative flex-1 my-1.5 mr-2">
        {/* Hour lines, so a bar can be read against the axis rather than
            estimated from its left edge. */}
        {ticks.slice(0, -1).map((t) => (
          <span
            key={t}
            className="absolute inset-y-0 w-px bg-ink-100"
            style={{ left: `${pctOf(t)}%` }}
            aria-hidden
          />
        ))}

        {/* What this work NEEDS, drawn behind the bars as the shape they are
            trying to fill. Rose when short, so an outline with nothing in it is
            the loudest thing in the row. */}
        {reqs.map((r) => {
          const g = barGeometry(r, axis);
          if (!g.width) return null;
          const short = r.coverage?.short > 0;
          const needed = r.coverage?.needed ?? r.peopleNeeded;
          const has = r.coverage?.covered ?? 0;
          const Tag = canEdit ? 'button' : 'div';
          return (
            <Tag
              key={r.id}
              type={canEdit ? 'button' : undefined}
              onClick={
                canEdit
                  ? () =>
                      // Everything the shift already needs to be: the work, and
                      // the hours the routine asked for — which may not be the
                      // shift type's defaults, because a show week feeds early.
                      onFill({
                        shiftTypeId: r.shiftTypeId,
                        startTime: r.startTime,
                        endTime: r.endTime,
                      })
                  : undefined
              }
              className={
                'absolute inset-y-0 rounded border-2 border-dashed flex items-center justify-center ' +
                (short ? 'border-rose-300 bg-rose-50/60' : 'border-hunter/30') +
                (canEdit ? ' hover:border-solid focus:border-solid cursor-pointer' : '')
              }
              style={{ left: `${g.left}%`, width: `${g.width}%` }}
              title={
                `Needs ${needed}, has ${has}` +
                (r.coverage?.worstFrom ? ` — thinnest ${r.coverage.worstFrom}–${r.coverage.worstTo}` : '') +
                (canEdit ? '. Click to put somebody on.' : '')
              }
              aria-label={
                canEdit
                  ? `Put somebody on ${row.name}, ${timeRange(r.startTime, r.endTime)}. Needs ${needed}, has ${has}.`
                  : undefined
              }
            >
              {short && g.width > 10 && (
                <span className="text-[11px] text-rose-700 pointer-events-none">
                  {needed - has} more
                </span>
              )}
            </Tag>
          );
        })}

        {/* Time off, behind everything: it is why a row is empty. */}
        {off.map((o) => {
          const g = o.allDay
            ? { left: 0, width: 100 }
            : barGeometry({ startTime: o.startTime, endTime: o.endTime }, axis);
          if (!g.width) return null;
          return (
            <div
              key={o.id}
              className={
                'absolute inset-y-0 rounded ' +
                (o.status === 'approved'
                  ? 'bg-sky-100 border border-sky-200'
                  : 'bg-amber-50 border border-dashed border-amber-300')
              }
              style={{ left: `${g.left}%`, width: `${g.width}%` }}
              title={
                (o.status === 'approved' ? 'Off' : 'Asked for this off') +
                (o.reason ? ` — ${o.reason}` : '')
              }
            >
              {g.width > 18 && (
                <span
                  className={
                    'block px-2 py-1 text-[11px] truncate ' +
                    (o.status === 'approved' ? 'text-sky-800' : 'text-amber-800')
                  }
                >
                  {o.status === 'approved' ? 'Off' : 'Asked off'}
                  {o.reason ? ` · ${o.reason}` : ''}
                </span>
              )}
            </div>
          );
        })}

        {/* A trainer's lessons: context, never editable from here. */}
        {teaching.map((l) => {
          if (!l.startTime || !l.endTime) return null;
          const g = barGeometry({ startTime: l.startTime, endTime: l.endTime }, axis);
          if (!g.width) return null;
          return (
            <div
              key={l.id}
              className="absolute inset-y-2 rounded border border-dashed border-ink-300"
              style={{ left: `${g.left}%`, width: `${g.width}%` }}
              title={`Teaching${l.riderName ? ` — ${l.riderName}` : ''}`}
            />
          );
        })}

        {/* Gaps in a person's own day. */}
        {holes.map((h) => {
          const g = barGeometry({ startTime: hhmm(h.start), endTime: hhmm(h.end) }, axis);
          if (!g.width) return null;
          return (
            <div
              key={h.start}
              className="absolute inset-y-4 border-t border-b border-dotted border-ink-300"
              style={{ left: `${g.left}%`, width: `${g.width}%` }}
              title={`Nothing scheduled ${tickLabel(h.start)} – ${tickLabel(h.end)}`}
            />
          );
        })}

        {row.shifts.map((s) => {
          const g = barGeometry(s, axis);
          if (!g.width) return null;
          const conflict = worstConflict(s);
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => onOpen(s)}
              disabled={!canEdit}
              className={
                'absolute rounded border px-1.5 overflow-hidden text-left ' +
                colorClass(s.color, 'card') +
                (canEdit ? ' hover:brightness-95 cursor-pointer' : ' cursor-default') +
                (conflict?.severity === 'blocking' ? ' ring-2 ring-rose-400' : '')
              }
              style={{
                left: `${g.left}%`,
                width: `${g.width}%`,
                // One lane fills the row; several share it, each keeping a
                // hairline of daylight so two abutting bars stay countable.
                top: lanes > 1 ? `calc(${(laneOf.get(s.id) ?? 0)} * (100% / ${lanes}) + 1px)` : '0.25rem',
                height: lanes > 1 ? `calc(100% / ${lanes} - 2px)` : 'calc(100% - 0.5rem)',
              }}
              title={
                `${timeRange(s.startTime, s.endTime)}${s.typeName ? ` · ${s.typeName}` : ''}` +
                (conflict ? ` — ${conflict.detail}` : '') +
                (s.notes ? ` — ${s.notes}` : '')
              }
            >
              <span className="block text-[11px] leading-tight truncate">
                {showNames ? s.name || 'Open shift' : s.typeName || 'At the barn'}
              </span>
              <span className="block text-[10px] leading-tight opacity-75 truncate tabular-nums">
                {timeRange(s.startTime, s.endTime)}
              </span>
              {s.unannounced && (
                <span
                  className="absolute top-0.5 right-0.5 w-1.5 h-1.5 rounded-full bg-amber-500"
                  aria-label="Not announced yet"
                />
              )}
            </button>
          );
        })}

        {/* Where we are now, on today only. */}
        {nowMinutes != null && nowMinutes >= axis.start && nowMinutes <= axis.end && (
          <span
            className="absolute inset-y-0 w-0.5 bg-rose-500/70 z-10"
            style={{ left: `${pctOf(nowMinutes)}%` }}
            aria-hidden
            title="Now"
          />
        )}

        {canEdit && row.shifts.length === 0 && reqs.length === 0 && (() => {
          // Over the gap it would fill, not across the whole row. An empty
          // Night check row offering "+ Add" in the middle of the afternoon
          // points at noon while the hole is at eight.
          const target = null;
          return (
            <button
              type="button"
              onClick={() => onFill(null)}
              className="absolute inset-y-0 rounded border border-transparent text-[11px] text-ink-300 hover:text-ink-700 hover:border-dashed hover:border-ink-300 focus:text-ink-700 focus:border-dashed focus:border-ink-300"
              style={
                target && target.width
                  ? { left: `${target.left}%`, width: `${target.width}%` }
                  : { left: 0, right: 0 }
              }
              aria-label={`Add a shift for ${row.name}`}
            >
              + Add
            </button>
          );
        })()}
      </div>
    </div>
  );
}

/** minutes -> "HH:MM", so geometry helpers can take a synthetic span. */
function hhmm(min) {
  const h = Math.floor(min / 60) % 24;
  return `${String(h).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`;
}

/** A row per body. Everyone appears — an empty row is how you spot a gap. */
function personRows(staff, dayShifts) {
  const rows = staff
    .slice()
    .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }))
    .map((s) => ({
      key: s.userId,
      userId: s.userId,
      name: s.name || 'Unnamed',
      shifts: sortByStart(dayShifts.filter((x) => x.userId === s.userId)),
    }));

  const open = sortByStart(dayShifts.filter((s) => !s.userId));
  if (open.length) {
    rows.unshift({ key: 'open', userId: null, name: 'Open shifts', open: true, shifts: open });
  }
  return rows;
}

/**
 * A row per kind of work, in the order the day runs rather than alphabetically:
 * reading down the rows should walk you through the day.
 */
function workRows(dayShifts, dayReqs = []) {
  const groups = new Map();
  const take = (key, name) => {
    let g = groups.get(key);
    if (!g) {
      g = { key, name, shifts: [], reqs: [] };
      groups.set(key, g);
    }
    return g;
  };

  for (const s of dayShifts) take(s.shiftTypeId || 'none', s.typeName || 'Unspecified').shifts.push(s);
  // Requirements first-class, not merely decoration on rows that already exist:
  // a job nobody is on has no shift to hang a row off, and that is precisely
  // the row worth seeing.
  for (const r of dayReqs) take(r.shiftTypeId || 'none', r.typeName || 'Unspecified').reqs.push(r);

  const rows = [...groups.values()];
  for (const r of rows) r.shifts = sortByStart(r.shifts);
  rows.sort((a, b) => {
    const start = (g) =>
      Math.min(
        toMinutes(g.shifts[0]?.startTime) ?? Infinity,
        toMinutes(g.reqs[0]?.startTime) ?? Infinity,
      );
    return (start(a) - start(b)) || a.name.localeCompare(b.name);
  });
  return rows;
}

function sortByStart(list) {
  return list.slice().sort((a, b) => a.startTime.localeCompare(b.startTime));
}
